/**
 * G21 product layout stability guard.
 * Keeps the native gallery visible and moves product accordions/details out of
 * the media column without rescanning the whole document after unrelated DOM work.
 */
(() => {
  'use strict';

  if (window.__G21_PRODUCT_LAYOUT_STABILITY__) return;
  window.__G21_PRODUCT_LAYOUT_STABILITY__ = true;

  const PAGE_SELECTOR = '.g21-product-page, .product-information';
  const unstableClasses = [
    'g21-media-layout-recovery',
    'g21-media-visibility-recovery',
    'g21-media-rescue-active',
  ];
  const observedPages = new WeakSet();
  const pendingPages = new Set();
  let correctionFrame = 0;

  const moveAdditionalBlocks = (page) => {
    const section = page.matches('.product-information')
      ? page
      : page.closest('.product-information') || page.querySelector('.product-information');
    if (!section) return;

    const productGrid = section.querySelector('product-component[data-product-grid-content]');
    const additional = section.querySelector('[data-g21-media-additional]');
    if (!productGrid || !additional) return;

    if (additional.parentElement !== section || productGrid.nextElementSibling !== additional) {
      productGrid.insertAdjacentElement('afterend', additional);
    }

    additional.classList.add('g21-product-additional--full-width');
    additional.style.setProperty('width', '100%');
    additional.style.setProperty('min-width', '0');
    additional.style.setProperty('grid-column', '1 / -1');
    additional.style.setProperty('margin-top', 'clamp(20px, 3vw, 40px)');
  };

  const stabilizePage = (page) => {
    if (!(page instanceof Element) || !page.isConnected) return;

    unstableClasses.forEach((className) => page.classList.remove(className));

    page.querySelectorAll('media-gallery').forEach((gallery) => {
      gallery.hidden = false;
      gallery.removeAttribute('aria-hidden');
      gallery.style.removeProperty('display');
      gallery.style.removeProperty('visibility');
      gallery.style.removeProperty('opacity');
    });

    page.querySelectorAll('[data-g21-media-fallback]').forEach((fallback) => {
      fallback.hidden = true;
      fallback.setAttribute('aria-hidden', 'true');
    });

    moveAdditionalBlocks(page);
  };

  const flushCorrections = () => {
    correctionFrame = 0;
    const pages = Array.from(pendingPages);
    pendingPages.clear();
    pages.forEach(stabilizePage);
  };

  const scheduleCorrection = (page) => {
    if (!(page instanceof Element)) return;
    pendingPages.add(page);
    if (!correctionFrame) correctionFrame = requestAnimationFrame(flushCorrections);
  };

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      const page = mutation.target instanceof Element ? mutation.target.closest(PAGE_SELECTOR) : null;
      if (!page) return;

      if (mutation.type === 'attributes') {
        if (unstableClasses.some((className) => page.classList.contains(className))) {
          scheduleCorrection(page);
        }
        return;
      }

      const relevantAddition = Array.from(mutation.addedNodes).some((node) =>
        node instanceof Element &&
        (node.matches('media-gallery, [data-g21-media-additional], [data-g21-media-fallback]') ||
          node.querySelector?.('media-gallery, [data-g21-media-additional], [data-g21-media-fallback]'))
      );
      if (relevantAddition) scheduleCorrection(page);
    });
  });

  const collectPages = (scope = document) => {
    const pages = [];
    if (scope instanceof Element && scope.matches(PAGE_SELECTOR)) pages.push(scope);
    scope.querySelectorAll?.(PAGE_SELECTOR).forEach((page) => pages.push(page));
    return pages;
  };

  const initialize = (scope = document) => {
    collectPages(scope).forEach((page) => {
      stabilizePage(page);
      if (observedPages.has(page)) return;
      observedPages.add(page);
      observer.observe(page, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class'],
      });
    });
  };

  const start = () => initialize(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  window.addEventListener('pagehide', () => {
    observer.disconnect();
    if (correctionFrame) cancelAnimationFrame(correctionFrame);
    pendingPages.clear();
  }, { once: true });
})();
